import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Alert, Button, Empty, Result, Skeleton } from 'antd';
import { errorMessage } from '../api/client';

interface QueryLike {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isFetching?: boolean;
  isPlaceholderData?: boolean;
  refetch?: () => unknown;
}

export const isNotFound = (e: unknown) => axios.isAxiosError(e) && (e.response?.status === 404 || e.response?.status === 400);

/** Inline error with a retry button, for cards and table bodies. */
export function LoadError({ error, onRetry, title }: { error: unknown; onRetry?: () => unknown; title?: string }) {
  return (
    <Alert
      type="error"
      showIcon
      title={title ?? 'Could not load this data'}
      description={errorMessage(error)}
      action={onRetry ? <Button size="small" onClick={() => void onRetry()}>Retry</Button> : undefined}
      style={{ textAlign: 'left' }}
    />
  );
}

/** Full-page state for a record that is missing or failed to load. */
export function RecordError({ error, what, backTo, backLabel, onRetry }: { error: unknown; what: string; backTo: string; backLabel: string; onRetry?: () => unknown }) {
  const missing = isNotFound(error);
  return (
    <div className="page">
      <Result
        status={missing ? '404' : 'warning'}
        title={missing ? `${what} not found` : `Could not load this ${what.toLowerCase()}`}
        subTitle={missing ? `It may have been deleted or merged into another record.` : errorMessage(error)}
        extra={[
          !missing && onRetry ? <Button key="retry" onClick={() => void onRetry()}>Retry</Button> : null,
          <Link key="back" to={backTo}><Button type="primary">{backLabel}</Button></Link>,
        ]}
      />
    </div>
  );
}

/** Skeleton shown while a record page loads. */
export function PageSkeleton() {
  return (
    <div className="page">
      <Skeleton active paragraph={{ rows: 6 }} />
    </div>
  );
}

/** Friendly empty state with an optional call to action. */
export function EmptyState({ description, action }: { description: ReactNode; action?: ReactNode }) {
  return (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} style={{ margin: '24px 0' }}>
      {action}
    </Empty>
  );
}

/** Table `locale` that shows the load error when the query failed, otherwise the empty state. */
export function tableLocale(query: QueryLike, empty: ReactNode) {
  return { emptyText: query.isError ? <LoadError error={query.error} onRetry={query.refetch} /> : typeof empty === 'string' ? <EmptyState description={empty} /> : empty };
}

/** Spinner for tables: first load, plus refetches that still show previous-page placeholder rows. */
export function tableLoading(query: QueryLike) {
  return query.isLoading || (!!query.isPlaceholderData && !!query.isFetching);
}
